import { useEffect, useState } from 'react'
import styled from 'styled-components'

type ThemeMode = 'light' | 'dark'

const TopCluster = styled.div`
  position: fixed;
  top: 2.4rem;
  right: 2.4rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 20;

  @media (max-width: 640px) {
    top: 1.6rem;
    right: 1.6rem;
  }
`

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 1.1rem;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  @media (max-width: 640px) {
    padding: 0.5rem 0.9rem;
  }
`

const AvatarButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const Avatar = styled.img`
  width: 4.8rem;
  height: 4.8rem;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border);
  background: var(--pill-bg);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);

  @media (max-width: 640px) {
    width: 4.2rem;
    height: 4.2rem;
  }
`

const Name = styled.span`
  font-weight: 700;
  color: var(--text);
  letter-spacing: 0.02rem;
  white-space: nowrap;
  font-size: 1.4rem;
`

const ToggleButton = styled.button<{ mode: ThemeMode }>`
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  .icon {
    width: 22px;
    height: 22px;
    transition: opacity 0.3s ease, transform 0.3s ease;
    position: absolute;
  }

  .sun {
    opacity: ${(props) => (props.mode === 'light' ? 1 : 0)};
    transform: ${(props) =>
      props.mode === 'light' ? 'rotate(0deg)' : 'rotate(180deg)'};
  }

  .moon {
    opacity: ${(props) => (props.mode === 'dark' ? 1 : 0)};
    transform: ${(props) =>
      props.mode === 'dark' ? 'rotate(0deg)' : 'rotate(-180deg)'};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 36px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 640px) {
    width: 4.6rem;
    height: 4.6rem;
  }
`

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  z-index: 30;
  padding: 1.6rem;
`

const LightboxImage = styled.img`
  max-width: min(540px, 90vw);
  width: 100%;
  height: auto;
  border-radius: 18px;
  border: 2px solid var(--border);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
`

export function ThemeToggle() {
  const getInitialTheme = (): ThemeMode => {
    if (typeof window === 'undefined') return 'dark'
    const stored = localStorage.getItem('theme')
    if (stored === 'light' || stored === 'dark') return stored
    const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches
    return prefersLight ? 'light' : 'dark'
  }

  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme())
  const [showFullImage, setShowFullImage] = useState(false)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowFullImage(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }

  return (
    <>
      <TopCluster>
        <Identity aria-label="Meher Sultana">
          <AvatarButton
            type="button"
            aria-label="Open full portrait"
            onClick={() => setShowFullImage(true)}
          >
            <Avatar src="/Images/MeherSultana2.jpeg" alt="Meher Sultana portrait" />
          </AvatarButton>
          <Name>Meher Sultana</Name>
        </Identity>
        <ToggleButton
          aria-label="Toggle theme"
          onClick={toggleTheme}
          mode={theme}
          type="button"
        >
          <svg
            className="icon sun"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
          <svg
            className="icon moon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        </ToggleButton>
      </TopCluster>

      {showFullImage && (
        <LightboxOverlay onClick={() => setShowFullImage(false)} aria-modal="true" role="dialog">
          <LightboxImage
            src="/Images/MeherSultana2.jpeg"
            alt="Meher Sultana portrait full size"
            onClick={(e) => e.stopPropagation()}
          />
        </LightboxOverlay>
      )}
    </>
  )
}
